// A git repository read from its `.git` folder: refs, commits, trees and file contents, the
// commit log in git's order, what each commit changed, and the history of one file. Read-only.
import { ObjectStore, toHex, type GitFiles, type ObjectStoreOptions } from './objects';

export interface GitPerson {
  name: string;
  email: string;
  /** Seconds since 1970. */
  time: number;
  /** The time zone as git writes it, such as `+0200`. */
  zone: string;
}

export interface GitCommit {
  id: string;
  tree: string;
  parents: string[];
  author: GitPerson;
  committer: GitPerson;
  message: string;
}

export type GitEntryKind = 'tree' | 'blob' | 'link' | 'submodule';

export interface GitTreeEntry {
  name: string;
  mode: string;
  id: string;
  kind: GitEntryKind;
}

export interface GitRef {
  /** The full name, such as `refs/heads/main`. */
  name: string;
  /** The commit it points at (annotated tags are followed to their commit). */
  id: string;
}

export interface GitHead {
  /** The branch checked out (`refs/heads/main`), or null when HEAD points at a commit. */
  ref: string | null;
  /** The commit checked out, or null in a repository without commits yet. */
  id: string | null;
}

export type FileChangeStatus = 'added' | 'deleted' | 'modified' | 'renamed';

export interface FileChange {
  status: FileChangeStatus;
  path: string;
  /** The path before a rename. */
  oldPath?: string;
  oldId: string | null;
  newId: string | null;
  /** What the path is: a file, a symbolic link or a submodule (the newer side's kind). */
  kind: Exclude<GitEntryKind, 'tree'>;
}

const decoder = new TextDecoder();

function kindOf(mode: string): GitEntryKind {
  if (mode === '40000' || mode === '040000') return 'tree';
  if (mode === '160000') return 'submodule';
  if (mode === '120000') return 'link';
  return 'blob';
}

export function parseTree(data: Uint8Array, idLength: number): GitTreeEntry[] {
  const out: GitTreeEntry[] = [];
  let pos = 0;
  while (pos < data.length) {
    const space = data.indexOf(0x20, pos);
    const nul = data.indexOf(0, space);
    if (space < 0 || nul < 0) break;
    const mode = decoder.decode(data.subarray(pos, space));
    out.push({ name: decoder.decode(data.subarray(space + 1, nul)), mode, id: toHex(data, nul + 1, idLength), kind: kindOf(mode) });
    pos = nul + 1 + idLength;
  }
  return out;
}

function parsePerson(text: string): GitPerson {
  const m = /^(.*?) <([^>]*)> (-?\d+) ([+-]\d{4})$/.exec(text.trim());
  if (!m) return { name: text.trim(), email: '', time: 0, zone: '+0000' };
  return { name: m[1]!, email: m[2]!, time: Number(m[3]), zone: m[4]! };
}

/** Header lines (with continuation lines joined) and the message after the blank line. */
function parseHeaders(text: string): { headers: [string, string][]; message: string } {
  const headers: [string, string][] = [];
  let pos = 0;
  while (pos < text.length) {
    const nl = text.indexOf('\n', pos);
    const line = text.slice(pos, nl < 0 ? text.length : nl);
    pos = nl < 0 ? text.length : nl + 1;
    if (line === '') break;
    if (line.startsWith(' ') && headers.length) headers[headers.length - 1]![1] += `\n${line.slice(1)}`;
    else {
      const space = line.indexOf(' ');
      headers.push(space < 0 ? [line, ''] : [line.slice(0, space), line.slice(space + 1)]);
    }
  }
  return { headers, message: text.slice(pos) };
}

export function parseCommit(id: string, data: Uint8Array): GitCommit {
  const { headers, message } = parseHeaders(decoder.decode(data));
  const value = (name: string) => headers.find(([n]) => n === name)?.[1] ?? '';
  return {
    id,
    tree: value('tree'),
    parents: headers.filter(([n]) => n === 'parent').map(([, v]) => v),
    author: parsePerson(value('author')),
    committer: parsePerson(value('committer')),
    message,
  };
}

/** Where `.git` points when it is a file (a linked worktree or a submodule): `gitdir: <path>`. */
export function gitDirPointer(text: string): string | null {
  const m = /^gitdir:\s*(.+?)\s*$/m.exec(text);
  return m ? m[1]! : null;
}

/** A max-heap of commits by committer date; equal dates come out in the order they went in, like git's. */
class CommitQueue {
  private readonly items: { commit: GitCommit; seq: number }[] = [];
  private seq = 0;

  get size(): number {
    return this.items.length;
  }

  private before(a: { commit: GitCommit; seq: number }, b: { commit: GitCommit; seq: number }): boolean {
    return a.commit.committer.time > b.commit.committer.time || (a.commit.committer.time === b.commit.committer.time && a.seq < b.seq);
  }

  push(commit: GitCommit): void {
    const items = this.items;
    items.push({ commit, seq: this.seq++ });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.before(items[i]!, items[parent]!)) break;
      [items[i], items[parent]] = [items[parent]!, items[i]!];
      i = parent;
    }
  }

  pop(): GitCommit {
    const items = this.items;
    const top = items[0]!;
    const last = items.pop()!;
    if (items.length) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let best = i;
        if (l < items.length && this.before(items[l]!, items[best]!)) best = l;
        if (r < items.length && this.before(items[r]!, items[best]!)) best = r;
        if (best === i) break;
        [items[i], items[best]] = [items[best]!, items[i]!];
        i = best;
      }
    }
    return top.commit;
  }
}

const sameEntry = (a: GitTreeEntry | null, b: GitTreeEntry | null) => (a === null ? b === null : b !== null && a.id === b.id && a.mode === b.mode);

export class GitRepository {
  /** Commits whose parents a shallow clone left out. */
  readonly shallow: Set<string>;
  private readonly commits = new Map<string, GitCommit>();
  private readonly trees = new Map<string, GitTreeEntry[]>();

  private constructor(
    readonly files: GitFiles,
    readonly objects: ObjectStore,
    shallow: string[],
  ) {
    this.shallow = new Set(shallow);
  }

  /** Opens the repository in a `.git` folder. Throws for formats it cannot read (reftable refs). */
  static async open(files: GitFiles, options: ObjectStoreOptions = {}): Promise<GitRepository> {
    const config = await readText(files, 'config');
    if (config === null && (await readText(files, 'HEAD')) === null) throw new Error('This is not a git folder.');
    if (/^\s*refstorage\s*=\s*reftable/im.test(config ?? '')) throw new Error('This repository keeps its refs in the reftable format, which modelviewer cannot read yet.');
    const sha256 = /^\s*objectformat\s*=\s*sha256/im.test(config ?? '');
    const objects = new ObjectStore(files, sha256 ? 32 : 20, options);
    await objects.refresh();
    const shallow = (await readText(files, 'shallow'))?.split('\n').map((l) => l.trim()).filter(Boolean) ?? [];
    return new GitRepository(files, objects, shallow);
  }

  get idLength(): number {
    return this.objects.idLength;
  }

  private isId(text: string): boolean {
    return text.length === this.idLength * 2 && /^[0-9a-f]+$/.test(text);
  }

  /** Reads the packs and the shallow list again, after git wrote to the repository. */
  async refresh(): Promise<void> {
    await this.objects.refresh();
    const shallow = (await readText(this.files, 'shallow'))?.split('\n').map((l) => l.trim()).filter(Boolean) ?? [];
    this.shallow.clear();
    for (const id of shallow) this.shallow.add(id);
  }

  private async packedRefs(): Promise<Map<string, string>> {
    const out = new Map<string, string>();
    for (const line of (await readText(this.files, 'packed-refs'))?.split('\n') ?? []) {
      const m = /^([0-9a-f]+) (\S+)$/.exec(line.trim());
      if (m) out.set(m[2]!, m[1]!);
    }
    return out;
  }

  /** What a ref (or `HEAD`, or a commit id) points at, following symbolic refs and annotated tags. */
  async resolve(name: string): Promise<string | null> {
    let current = name;
    for (let depth = 0; depth < 10; depth++) {
      if (this.isId(current)) return this.peel(current);
      const text = (await readText(this.files, current))?.trim();
      if (text?.startsWith('ref:')) {
        current = text.slice(4).trim();
        continue;
      }
      if (text && this.isId(text)) return this.peel(text);
      const packed = (await this.packedRefs()).get(current);
      return packed ? this.peel(packed) : null;
    }
    return null;
  }

  /** Follows annotated tags to what they tag. */
  private async peel(id: string): Promise<string> {
    let current = id;
    for (let depth = 0; depth < 10; depth++) {
      const obj = await this.objects.read(current);
      if (obj?.type !== 'tag') return current;
      const target = /^object (\S+)/m.exec(decoder.decode(obj.data));
      if (!target) return current;
      current = target[1]!;
    }
    return current;
  }

  async head(): Promise<GitHead> {
    const text = (await readText(this.files, 'HEAD'))?.trim() ?? '';
    const ref = text.startsWith('ref:') ? text.slice(4).trim() : null;
    return { ref, id: await this.resolve('HEAD') };
  }

  /** Branches, remote branches and tags, by name. */
  async refs(): Promise<GitRef[]> {
    const names = new Map<string, string>();
    for (const [name, id] of await this.packedRefs()) if (name.startsWith('refs/')) names.set(name, id);
    const walk = async (folder: string): Promise<void> => {
      for (const entry of await this.files.list(folder)) {
        const path = `${folder}/${entry}`;
        const sub = await this.files.list(path);
        if (sub.length) await walk(path);
        else {
          const text = (await readText(this.files, path))?.trim();
          if (text) names.set(path, text);
        }
      }
    };
    for (const folder of ['refs/heads', 'refs/remotes', 'refs/tags']) await walk(folder);
    const out: GitRef[] = [];
    for (const name of [...names.keys()].sort()) {
      // A remote's HEAD only names its default branch.
      if (/^refs\/remotes\/[^/]+\/HEAD$/.test(name)) continue;
      const id = await this.resolve(name);
      if (id) out.push({ name, id });
    }
    return out;
  }

  async commit(id: string): Promise<GitCommit | null> {
    const known = this.commits.get(id);
    if (known) return known;
    const obj = await this.objects.read(id);
    if (!obj || obj.type !== 'commit') return null;
    const commit = parseCommit(id, obj.data);
    if (this.commits.size > 200_000) this.commits.clear();
    this.commits.set(id, commit);
    return commit;
  }

  async tree(id: string): Promise<GitTreeEntry[] | null> {
    const known = this.trees.get(id);
    if (known) return known;
    const obj = await this.objects.read(id);
    if (!obj || obj.type !== 'tree') return null;
    const entries = parseTree(obj.data, this.idLength);
    if (this.trees.size > 50_000) this.trees.clear();
    this.trees.set(id, entries);
    return entries;
  }

  async blob(id: string): Promise<Uint8Array | null> {
    const obj = await this.objects.read(id);
    return obj?.type === 'blob' ? obj.data : null;
  }

  /** The entry at a path below a tree (`src/Shop/Order.cs`), or null. The empty path is the tree itself. */
  async entryAt(treeId: string, path: string): Promise<GitTreeEntry | null> {
    if (path === '') return { name: '', mode: '40000', id: treeId, kind: 'tree' };
    let entries = await this.tree(treeId);
    const segments = path.split('/');
    for (let i = 0; i < segments.length; i++) {
      const entry = entries?.find((e) => e.name === segments[i]);
      if (!entry) return null;
      if (i === segments.length - 1) return entry;
      if (entry.kind !== 'tree') return null;
      entries = await this.tree(entry.id);
    }
    return null;
  }

  /** The entries of a folder in a commit, or null when it has no such folder. */
  async folder(commitId: string, path: string): Promise<GitTreeEntry[] | null> {
    const commit = await this.commit(commitId);
    if (!commit) return null;
    const entry = await this.entryAt(commit.tree, path);
    return entry?.kind === 'tree' ? this.tree(entry.id) : null;
  }

  /** Every file path in a commit (not folders), sorted the way git sorts them. */
  async paths(commitId: string): Promise<string[]> {
    const commit = await this.commit(commitId);
    if (!commit) return [];
    const out: string[] = [];
    const walk = async (treeId: string, prefix: string): Promise<void> => {
      for (const e of (await this.tree(treeId)) ?? []) {
        if (e.kind === 'tree') await walk(e.id, `${prefix}${e.name}/`);
        else out.push(`${prefix}${e.name}`);
      }
    };
    await walk(commit.tree, '');
    return out;
  }

  /**
   * The commits reachable from the starts, newest first by commit date, like `git log`. Parents a
   * shallow clone left out (or that are missing) end the walk there.
   */
  async *log(starts: string[]): AsyncGenerator<GitCommit> {
    const queue = new CommitQueue();
    const seen = new Set<string>();
    for (const id of starts) {
      if (seen.has(id)) continue;
      seen.add(id);
      const commit = await this.commit(id);
      if (commit) queue.push(commit);
    }
    while (queue.size) {
      const commit = queue.pop();
      yield commit;
      if (this.shallow.has(commit.id)) continue;
      for (const parent of commit.parents) {
        if (seen.has(parent)) continue;
        seen.add(parent);
        const next = await this.commit(parent);
        if (next) queue.push(next);
      }
    }
  }

  /** What a commit changed, compared with its first parent (everything is added in a first commit). */
  async changes(commitId: string): Promise<FileChange[]> {
    const commit = await this.commit(commitId);
    if (!commit) return [];
    const parentId = this.shallow.has(commit.id) ? undefined : commit.parents[0];
    const parent = parentId ? await this.commit(parentId) : null;
    return this.diffTrees(parent?.tree ?? null, commit.tree);
  }

  /** The files that differ between two trees, with exact renames (the same content moved) paired up. */
  async diffTrees(before: string | null, after: string | null): Promise<FileChange[]> {
    const out: FileChange[] = [];
    await this.walkDiff(before, after, '', out);
    return pairRenames(out, this.idLength).sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  }

  private async walkDiff(before: string | null, after: string | null, prefix: string, out: FileChange[]): Promise<void> {
    if (before === after) return;
    const a = before ? ((await this.tree(before)) ?? []) : [];
    const b = after ? ((await this.tree(after)) ?? []) : [];
    const byName = new Map(a.map((e) => [e.name, e]));
    const names = [...new Set([...a.map((e) => e.name), ...b.map((e) => e.name)])];
    const inB = new Map(b.map((e) => [e.name, e]));
    for (const name of names) {
      const x = byName.get(name) ?? null;
      const y = inB.get(name) ?? null;
      if (sameEntry(x, y)) continue;
      const path = `${prefix}${name}`;
      const xTree = x?.kind === 'tree';
      const yTree = y?.kind === 'tree';
      if (xTree || yTree) {
        await this.walkDiff(xTree ? x.id : null, yTree ? y.id : null, `${path}/`, out);
        if (x && !xTree) out.push({ status: 'deleted', path, oldId: x.id, newId: null, kind: x.kind as FileChange['kind'] });
        if (y && !yTree) out.push({ status: 'added', path, oldId: null, newId: y.id, kind: y.kind as FileChange['kind'] });
        continue;
      }
      if (x && y) out.push({ status: 'modified', path, oldId: x.id, newId: y.id, kind: y.kind as FileChange['kind'] });
      else if (x) out.push({ status: 'deleted', path, oldId: x.id, newId: null, kind: x.kind as FileChange['kind'] });
      else if (y) out.push({ status: 'added', path, oldId: null, newId: y.id, kind: y.kind as FileChange['kind'] });
    }
  }

  /**
   * Whether a commit changed a path: it differs from every parent (a merge that only took one
   * side's version does not count; the commits on that side do).
   */
  async touches(commit: GitCommit, path: string): Promise<boolean> {
    const mine = await this.entryAt(commit.tree, path);
    const parents = this.shallow.has(commit.id) ? [] : commit.parents;
    if (!parents.length) return mine !== null;
    for (const id of parents) {
      const parent = await this.commit(id);
      // A parent the clone does not have: compare with nothing, as git does at a shallow boundary.
      const theirs = parent ? await this.entryAt(parent.tree, path) : null;
      if (sameEntry(mine, theirs)) return false;
    }
    return true;
  }
}

/** The empty file is in every repository many times over; moving one is not a rename. */
const EMPTY_BLOB = new Set(['e69de29bb2d1d6434b8b29ae775ad8c2e48c5391', '473a0f4c3be8a93681a267e3b1e9a7dcda1185436fe141f7749120a303721813']);

function pairRenames(changes: FileChange[], idLength: number): FileChange[] {
  const deleted = new Map<string, FileChange[]>();
  for (const c of changes) {
    if (c.status === 'deleted' && c.kind === 'blob' && !EMPTY_BLOB.has(c.oldId!) && c.oldId!.length === idLength * 2) {
      const list = deleted.get(c.oldId!) ?? [];
      list.push(c);
      deleted.set(c.oldId!, list);
    }
  }
  if (!deleted.size) return changes;
  const used = new Set<FileChange>();
  const out: FileChange[] = [];
  const base = (p: string) => p.slice(p.lastIndexOf('/') + 1);
  for (const c of changes) {
    if (c.status !== 'added' || c.kind !== 'blob') continue;
    const candidates = deleted.get(c.newId!)?.filter((d) => !used.has(d));
    if (!candidates?.length) continue;
    const from = candidates.find((d) => base(d.path) === base(c.path)) ?? candidates[0]!;
    used.add(from);
    used.add(c);
    out.push({ status: 'renamed', path: c.path, oldPath: from.path, oldId: from.oldId, newId: c.newId, kind: c.kind });
  }
  return [...changes.filter((c) => !used.has(c)), ...out];
}

async function readText(files: GitFiles, path: string): Promise<string | null> {
  const bytes = await files.read(path);
  return bytes ? decoder.decode(bytes) : null;
}
